import {
  type FC,
  useRef,
  useState,
  type KeyboardEvent,
  useEffect,
  useMemo,
} from 'react';

import { Iconsvg, PillSuggestion } from '@wk/components-react16';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/shared/ui/Command';
import { Command as CommandPrimitive } from 'cmdk';
import { List, type RowComponentProps } from 'react-window';

import './MultiSelect.css';
import { Portal } from '@radix-ui/react-portal';
import { cn } from '@/lib/utils';

interface MultiSelectProps {
  options: {
    value: string;
    label: string;
    render?: (option: { value: string; label: string }) => React.ReactNode;
  }[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  filter?: (value: string, search: string, keywords?: string[]) => number;
  size?: 'small' | 'medium' | 'large';
  itemHeight?: number;
  className?: string;
}

const ITEM_HEIGHT = 36;

type RowProps = {
  options: {
    value: string;
    label: string;
    render?: (option: { value: string; label: string }) => React.ReactNode;
  }[];
  value: string[];
  setInputValue: (value: string) => void;
  onChange: (value: string[]) => void;
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
};

function RowItem({ index, style, options, value, setInputValue, onChange, highlightedIndex, setHighlightedIndex }: RowComponentProps<RowProps>) {
  const option = options[index];
  const isHighlighted = index === highlightedIndex;

  return (
    <CommandItem
      style={style}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseEnter={() => {
        setHighlightedIndex(index);
      }}
      onSelect={() => {
        setInputValue('');
        if (value.includes(option.value)) {
          onChange(value.filter((v) => v !== option.value));
        } else {
          onChange([...value, option.value]);
        }
      }}
      className={`cursor-pointer ${isHighlighted
          ? 'bg-[#005b92] text-white'
          : 'hover:bg-[#e6f2f9]'
        }`}
    >
      <span className="w-full flex items-center justify-between">
        {option.render ? option.render(option) : option.label}
        {value.includes(option.value) && <Iconsvg name="check" />}
      </span>
    </CommandItem>
  );
}

const MultiSelect: FC<MultiSelectProps> = ({
  options,
  value = [],
  onChange,
  placeholder = 'Select...',
  filter,
  size = 'large',
  itemHeight = ITEM_HEIGHT,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const handleUnselect = (_value: string) => {
    onChange(value.filter((s) => s !== _value));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current;
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          onChange(value.slice(0, -1));
        }
      }
      // This is not a default behaviour of the <input /> field
      if (e.key === 'Escape') {
        input.blur();
        setIsOpen(false);
      }

      // Arrow key navigation
      if (isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0;
            return nextIndex;
          });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            const nextIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1;
            return nextIndex;
          });
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
          e.preventDefault();
          const selectedOption = filteredOptions[highlightedIndex];
          if (selectedOption) {
            setInputValue('');
            if (value.includes(selectedOption.value)) {
              onChange(value.filter((v) => v !== selectedOption.value));
            } else {
              onChange([...value, selectedOption.value]);
            }
          }
        }
      } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
    }
  };

  // Calculate dropdown position when component mounts or when isOpen changes
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const selected = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const rawSearch = (inputValue || '').trim();
    if (!rawSearch) return options;

    if (filter) {
      const scored = options
        .map((opt) => ({
          opt,
          score: filter(opt.label ?? opt.value, rawSearch.toLowerCase()),
        }))
        .filter((x) => (typeof x.score === 'number' ? x.score : 0) > 0)
        .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
        .map((x) => x.opt);
      return scored;
    }

    let searchRegex: RegExp | null = null;
    try {
      const escapedSearch = rawSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      searchRegex = new RegExp(escapedSearch, 'i');
    } catch {
      searchRegex = null;
    }

    return options.filter((opt) => {
      const label = opt.label ?? '';
      const valueStr = opt.value ?? '';

      if (searchRegex) {
        return searchRegex.test(label) || searchRegex.test(valueStr);
      }

      const lower = rawSearch.toLowerCase();
      return (
        label.toLowerCase().includes(lower) ||
        valueStr.toLowerCase().includes(lower)
      );
    });
  }, [options, inputValue, filter]);

  // Reset highlighted index when options change or dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
    } else {
      setHighlightedIndex(-1);
    }
  }, [isOpen, filteredOptions.length]);

  return (
    <Command
      shouldFilter={false}
      onKeyDown={handleKeyDown}
      className={cn('overflow-visible bg-transparent min-w-2xs max-w-lg', className)}
    >
      <div className="group border border-input hover:border-[#005b92] hover:shadow-[0_0_0_.0625rem_#005b92] focus-within:border-[#000] focus-within:shadow-[0_0_0_.0625rem_#000] focus-within:hover:border-[#000] focus-within:hover:shadow-[0_0_0_.0625rem_#000] text-sm">
        <div
          className={cn([
            'flex min-w-0 items-center gap-1 px-[15px] py-1 min-h-10',
            size === 'small' && 'min-h-8',
            size === 'medium' && 'min-h-10',
            size === 'large' && 'min-h-12',
          ])}
        >
          {selected.length > (size === 'small' ? 1 : 2) ? (
            <>
              <PillSuggestion>{selected.length - 1} +</PillSuggestion>
              <PillSuggestion>
                <span className="truncate max-w-[120px]">
                  {selected[selected.length - 1].label}
                </span>
                <span
                  className="ml-1 rounded-full outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUnselect(selected[selected.length - 1].value);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() =>
                    handleUnselect(selected[selected.length - 1].value)
                  }
                >
                  <span className="h-3 w-3 text-muted-foreground hover:text-foreground">
                    <Iconsvg name="close" />
                  </span>
                </span>
              </PillSuggestion>
            </>
          ) : (
            selected.map((option) => {
              return (
                <PillSuggestion key={option.value}>
                  <span className="truncate max-w-[100px]">{option.label}</span>
                  <span
                    className="ml-1 rounded-full outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(option.value);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleUnselect(option.value)}
                  >
                    <span className="h-3 w-3 text-muted-foreground hover:text-foreground">
                      <Iconsvg name="close" />
                    </span>
                  </span>
                </PillSuggestion>
              );
            })
          )}
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => {
              setIsOpen(false);
              setHighlightedIndex(-1);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={selected.length == 0 ? placeholder : ''}
            className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>
      <div className="relative" ref={containerRef}>
        <CommandList>
          {isOpen ? (
            <Portal asChild>
              <div
                className="fixed border-[.0625rem_solid_var(--cg-dropdown-menu-border-color,_#dadada)] bg-white text-popover-foreground shadow-md outline-none animate-in"
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  width: `${dropdownPosition.width}px`,
                  zIndex: 2000,
                }}
              >
                <CommandGroup>
                  <List
                    key={`${filteredOptions.length}-${inputValue}`}
                    style={{
                      height: Math.min(300, filteredOptions.length * itemHeight),
                      width: dropdownPosition.width,
                    }}
                    rowComponent={RowItem}
                    rowCount={filteredOptions.length}
                    rowHeight={itemHeight}
                    rowProps={{
                      options: filteredOptions,
                      value,
                      setInputValue,
                      onChange,
                      highlightedIndex,
                      setHighlightedIndex,
                    }}
                  />
                </CommandGroup>
              </div>
            </Portal>
          ) : null}
        </CommandList>
      </div>
    </Command>
  );
};

export default MultiSelect;
